/*
  FINAL REFINED SIDEBAR COMPONENT
  - Headless PrimeReact Sidebar with Dark Theme.
  - Robust Fix for "Blur" issue: modal disabled on desktop.
*/

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as heroIcons from "@heroicons/react/24/outline";
import { Sidebar as PRSidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { useDispatch } from "react-redux";
import logoutAction from "../../redux/actions/auth/LogoutAction";
import { classNames, role_name } from "../../utils";
import { useConfirmation } from "./ConfirmationDialog";

const Sidebar = ({ openMenu, setOpenMenu }) => {
  const [menuOptions, setMenuOptions] = React.useState([]);
  const [expandedMenus, setExpandedMenus] = React.useState({});
  const showConfirmation = useConfirmation();
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  React.useEffect(() => {
    const resources = JSON.parse(localStorage.getItem("resources"));
    setMenuOptions(resources || []);

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onLogout = (e) => {
    e.preventDefault();
    showConfirmation({
      header: "Logout Confirmation",
      message: "Are you sure you want to logout?",
      icon: "pi pi-power-off text-red-500 text-2xl mr-3",
      acceptLabel: "Yes",
      rejectLabel: "No",
      acceptClassName:
        "bg-red-600 text-white hover:bg-red-700 px-8 py-2 rounded-md font-semibold transition-all duration-200 border-none shadow-sm",
      rejectClassName:
        "bg-lightBlue hover:bg-slate-500 text-gray-700  px-8 py-2 rounded-md font-semibold transition-all duration-200 border-none",
      accept: () => {
        dispatch(logoutAction());
        navigate("/login");
      },
    });
  };

  const toggleMenu = (resourceId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [resourceId]: !prev[resourceId],
    }));
  };

  const userName = localStorage.getItem("first_name") || "User";
  const role = role_name() ?? "No role found";

  const isMini = isDesktop && !openMenu;

  const renderMenuItem = (item, isSubmenu = false) => {
    const SelectedIcon = heroIcons[item.icon] || heroIcons.CubeIcon;
    const isActive = location.pathname === item.slug;
    const hasChildren = item.child && item.child.length > 0;
    const isExpanded = expandedMenus[item.resource_id];

    return (
      <div key={item.resource_id} className="w-full">
        <div
          className={classNames(
            "flex items-center transition-all duration-200 group cursor-pointer no-underline",
            isMini
              ? "justify-center px-0 py-3"
              : "gap-3 px-2 py-2.5 rounded-lg",
            isActive
              ? "bg-volcanicSand text-white shadow-md active-menu-glow"
              : "text-suvaGray hover:bg-volcanicSand/50 hover:text-white",
            !isMini && (isSubmenu ? "ml-6 text-sm" : "my-0.5"),
          )}
          onClick={(e) => {
            if (hasChildren) {
              toggleMenu(item.resource_id);
            } else if (item.slug) {
              navigate(item.slug);
              if (!isDesktop) setOpenMenu(false);
            }
          }}
          title={isMini ? item.resource_name : ""}
        >
          <SelectedIcon
            className={classNames(
              "h-5 w-5 flex-shrink-0 transition-colors",
              isActive ? "text-white" : "text-suvaGray group-hover:text-white",
            )}
          />
          {!isMini && (
            <>
              <span className="flex-1 truncate font-medium">
                {item.resource_name}
              </span>
              {item.resource_name === "Messages" && (
                <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full ring-2 ring-maritimeBlue">
                  3
                </span>
              )}
              {hasChildren && (
                <heroIcons.ChevronDownIcon
                  className={classNames(
                    "h-4 w-4 transition-transform duration-300",
                    isExpanded ? "rotate-180" : "",
                  )}
                />
              )}
            </>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div
            className={classNames(
              "mt-1 space-y-1 animate-in fade-in slide-in-from-top-1 duration-200",
              isMini ? "flex flex-col items-center" : "",
            )}
          >
            {item.child.map((child) => renderMenuItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <PRSidebar
      visible={isDesktop ? true : openMenu}
      onHide={() => setOpenMenu(false)}
      content={({ hide }) => (
        <div
          className={classNames(
            "flex flex-col h-full bg-maritimeBlue shadow-2xl overflow-hidden border-r border-volcanicSand/20 mt-20 transition-all duration-300 ease-in-out",
            isDesktop ? (openMenu ? "w-72" : "w-20") : "w-full sm:w-72",
          )}
        >
          <div className="h-4"></div>
          <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-2 custom-scrollbar">
            <div className="space-y-1">
              {menuOptions.map((item) => renderMenuItem(item))}
            </div>
          </div>

          <div className="mt-auto border-t border-volcanicSand/30 p-2 sm:p-4 bg-maritimeBlue/80 backdrop-blur-md">
            <div
              className={classNames(
                "flex items-center rounded-xl transition-all cursor-pointer group hover:bg-volcanicSand/40",
                isMini
                  ? "flex-col gap-4 py-4 justify-center"
                  : "justify-between p-2.5 bg-volcanicSand/20 border border-volcanicSand/10",
              )}
            >
              <div
                className={classNames(
                  "flex items-center",
                  isMini ? "flex-col" : "gap-3",
                )}
              >
                <div className="relative">
                  <Avatar
                    label={userName[0]}
                    shape="circle"
                    className="bg-blue-600 text-black font-bold text-sm h-10 w-10 shadow-lg"
                  />
                  <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-maritimeBlue rounded-full"></div>
                </div>
                {!isMini && (
                  <div className="flex flex-col overflow-hidden max-w-[120px]">
                    <span className="text-sm font-bold text-white group-hover:text-blue-200 transition-colors tracking-tight truncate">
                      {userName}
                    </span>
                    <span className="text-sm text-suvaGray font-medium truncate">
                      {role}
                    </span>
                  </div>
                )}
              </div>
              <Button
                // tooltip={isMini ? "Logout" : "Logout"}
                tooltipOptions={
                  isMini ? { position: "right" } : { position: "top" }
                }
                icon="pi pi-power-off"
                onClick={onLogout}
                className={classNames(
                  "p-button-rounded p-button-text transition-colors",
                  isMini
                    ? "text-suvaGray hover:text-red-400"
                    : "text-suvaGray hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0",
                )}
              />
            </div>
          </div>
        </div>
      )}
      modal={!isDesktop} // DISABLE MODAL ON DESKTOP - This fixes the "blur" and mask issue
      dismissable={!isDesktop}
      blockScroll={!isDesktop}
      className={classNames(
        "z-[999] border-none bg-transparent transition-all duration-300 ease-in-out p-0",
        isDesktop ? (openMenu ? "!w-72" : "!w-20") : "!w-full sm:!w-72",
      )}
      style={{ width: "auto" }} // Allow Tailwind to control width
      showCloseIcon={false}
      maskClassName={
        isDesktop ? "hidden !pointer-events-none" : "backdrop-blur-sm"
      } // Force hide on desktop
    />
  );
};

export default Sidebar;
